import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/lib/auth";
import { ProtectedRoute } from "@/components/ProtectedRoute";

// Eagerly load the landing page for fastest FCP/LCP
import Index from "./pages/Index";

// Lazy load all other pages
const PricingPage = lazy(() => import("./pages/Pricing"));
const ConversionPage = lazy(() => import("./pages/use-cases/Conversion"));
const CartRecoveryPage = lazy(() => import("./pages/use-cases/CartRecovery"));
const ContentPersonalizationPage = lazy(() => import("./pages/use-cases/ContentPersonalization"));
const ABTestingPage = lazy(() => import("./pages/use-cases/ABTesting"));
const PopupsPage = lazy(() => import("./pages/widgets/Popups"));
const BannersPage = lazy(() => import("./pages/widgets/Banners"));
const SocialProofPage = lazy(() => import("./pages/widgets/SocialProof"));
const SegmentationPage = lazy(() => import("./pages/features/Segmentation"));
const AnalyticsFeaturesPage = lazy(() => import("./pages/features/Analytics"));
const IntegrationsPage = lazy(() => import("./pages/features/Integrations"));
const AIFeaturesPage = lazy(() => import("./pages/features/AI"));
const JourneysFeaturesPage = lazy(() => import("./pages/features/Journeys"));
const DocumentationPage = lazy(() => import("./pages/resources/Documentation"));
const BlogPage = lazy(() => import("./pages/resources/Blog"));
const BlogPostPage = lazy(() => import("./pages/resources/BlogPost"));
const CaseStudiesPage = lazy(() => import("./pages/resources/CaseStudies"));
const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Stores = lazy(() => import("./pages/Stores"));
const Campaigns = lazy(() => import("./pages/Campaigns"));
const Segments = lazy(() => import("./pages/Segments"));
const Analytics = lazy(() => import("./pages/Analytics"));
const Journeys = lazy(() => import("./pages/Journeys"));
const Landings = lazy(() => import("./pages/Landings"));
const Agent = lazy(() => import("./pages/Agent"));
const AgentSettings = lazy(() => import("./pages/AgentSettings"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const NotFound = lazy(() => import("./pages/NotFound"));
const LandingPageView = lazy(() => import("./pages/LandingPageView"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AuthProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<div className="min-h-screen bg-background" />}>
            <Routes>
              {/* Marketing pages */}
              <Route path="/" element={<Index />} />
              <Route path="/pricing" element={<PricingPage />} />
              
              {/* Use case pages */}
              <Route path="/use-cases/conversion" element={<ConversionPage />} />
              <Route path="/use-cases/cart-recovery" element={<CartRecoveryPage />} />
              <Route path="/use-cases/content-personalization" element={<ContentPersonalizationPage />} />
              <Route path="/use-cases/ab-testing" element={<ABTestingPage />} />
              
              {/* Widget pages */}
              <Route path="/widgets/popups" element={<PopupsPage />} />
              <Route path="/widgets/banners" element={<BannersPage />} />
              <Route path="/widgets/social-proof" element={<SocialProofPage />} />
              
              {/* Feature pages */}
              <Route path="/features/segmentation" element={<SegmentationPage />} />
              <Route path="/features/analytics" element={<AnalyticsFeaturesPage />} />
              <Route path="/features/integrations" element={<IntegrationsPage />} />
              <Route path="/features/ai" element={<AIFeaturesPage />} />
              <Route path="/features/journeys" element={<JourneysFeaturesPage />} />
              
              {/* Resource pages */}
              <Route path="/resources/documentation" element={<DocumentationPage />} />
              <Route path="/resources/blog" element={<BlogPage />} />
              <Route path="/resources/blog/:slug" element={<BlogPostPage />} />
              <Route path="/resources/case-studies" element={<CaseStudiesPage />} />
              
              {/* Auth */}
              <Route path="/auth" element={<Auth />} />
              
              {/* Dashboard (protected) */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/agent"
                element={
                  <ProtectedRoute>
                    <Agent />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/agent/settings"
                element={
                  <ProtectedRoute>
                    <AgentSettings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/stores"
                element={
                  <ProtectedRoute>
                    <Stores />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/onboarding"
                element={
                  <ProtectedRoute>
                    <Onboarding />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/campaigns"
                element={
                  <ProtectedRoute>
                    <Campaigns />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/segments"
                element={
                  <ProtectedRoute>
                    <Segments />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/analytics"
                element={
                  <ProtectedRoute>
                    <Analytics />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/journeys"
                element={
                  <ProtectedRoute>
                    <Journeys />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard/landings"
                element={
                  <ProtectedRoute>
                    <Landings />
                  </ProtectedRoute>
                }
              />
              
              {/* Public landing page renderer */}
              <Route path="/lp" element={<LandingPageView />} />
              
              {/* Catch-all */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
